import { unionBy } from "lodash";
import {
  ADD_FILTER_FAILURE,
  ADD_FILTER_REQUEST,
  ADD_FILTER_SUCCESS,
  CLEAR_FILTERS,
  CLEAR_LEADS,
  DELETE_FILTER_FAILURE,
  DELETE_FILTER_REQUEST,
  DELETE_FILTER_SUCCESS,
  DELETE_LEAD_LIST_SUCCESS,
  GET_FILTERED_LEADS_FAILURE,
  GET_FILTERED_LEADS_REQUEST,
  GET_FILTERED_LEADS_SUCCESS,
  GET_FILTERS_FAILURE,
  GET_FILTERS_REQUEST,
  GET_FILTERS_STATUS_SUCCESS,
  GET_FILTERS_SUCCESS,
  GET_LEAD_INFO_FAILURE,
  GET_LEAD_INFO_REQUEST,
  GET_LEAD_INFO_SUCCESS,
  GET_LEAD_LISTS_FAILURE,
  GET_LEAD_LISTS_REQUEST,
  GET_LEAD_LISTS_SUCCESS,
  GET_STATS_FAILURE,
  GET_STATS_REQUEST,
  GET_STATS_SUCCESS,
  TOGGLE_FILTER_FAILURE,
  TOGGLE_FILTER_REQUEST,
  TOGGLE_FILTER_SUCCESS,
  UPLOAD_CSV_FAILURE,
  UPLOAD_CSV_REQUEST,
  UPLOAD_CSV_SUCCESS,
  SYNC_LEAD_STATUS_SUCCESS,
  SYNC_STATS_SUCCESS,
  LOAD_MORE_LEAD_LISTS_REQUEST,
  LOAD_MORE_LEAD_LISTS_FAILURE,
  LOAD_MORE_LEAD_LISTS_SUCCESS,
} from "./leadActions";

export interface LeadFile {
  id: string;
  deleted_at: string | null;
  deleted_by_cascade: boolean;
  created_at: string;
  updated_at: string;
  list_name: string;
  source_name: string;
  attachment: string;
  file_type: string | null;
  file_name: string;
  created_by_user_id: number;
  process_started_at: string | null;
  process_completed_at: string | null;
  status: number;
  lead_analysis: any | null;
  lead_analysis_file_key: string | null;
  retry_count: number;
  notes: Record<string, any>;
  row_count: number;
  processed_rows: number;
  created_by: any | null;
}

export interface LeadState {
  uploadingCSV: boolean;
  leadLists: LeadFile[];
  leadListsMetaData: {
    count: number;
    page_size: number;
    page_count: number;
    has_next: boolean;
  };
  leadListsLoading: boolean;
  filters: any[];
  filtersMetaData: {
    count: number;
    page_size: number;
    page_count: number;
    has_next: boolean;
  };
  filtersLoading: boolean;
  addFilterLoading: boolean;
  toggleFilterLoading: boolean;
  deleteFilterLoading: boolean;
  filteredLeads: any[];
  filteredLeadsMetaData: {
    count: number;
    page_size: number;
    page_count: number;
    has_next: boolean;
  };
  filteredLeadsLoading: boolean;
  stats: any;
  statsLoading: boolean;
  leadInfo: any;
  leadInfoLoading: boolean;
}

const initialState: LeadState = {
  uploadingCSV: false,
  leadLists: [],
  leadListsMetaData: {
    count: 0,
    page_size: 0,
    page_count: 0,
    has_next: false,
  },
  leadListsLoading: false,
  filters: [],
  filtersMetaData: {
    count: 0,
    page_size: 0,
    page_count: 0,
    has_next: false,
  },
  filtersLoading: false,
  addFilterLoading: false,
  toggleFilterLoading: false,
  deleteFilterLoading: false,
  filteredLeads: [],
  filteredLeadsMetaData: {
    count: 0,
    page_size: 0,
    page_count: 0,
    has_next: false,
  },
  filteredLeadsLoading: false,
  stats: {},
  statsLoading: false,
  leadInfo: {},
  leadInfoLoading: false,
}

export const leadReducers = (state = initialState, action: any) => {
  switch (action.type) {
    case UPLOAD_CSV_REQUEST:
      return { ...state, uploadingCSV: true };
    case UPLOAD_CSV_SUCCESS:
      return { ...state, uploadingCSV: false };
    case UPLOAD_CSV_FAILURE:
      return { ...state, uploadingCSV: false };
    case GET_LEAD_LISTS_REQUEST:
      return { ...state, leadListsLoading: true };
    case GET_LEAD_LISTS_SUCCESS:
      return { ...state, leadLists: action.response.data, leadListsMetaData: action.response.metadata, leadListsLoading: false };
    case GET_LEAD_LISTS_FAILURE:
      return { ...state, leadListsLoading: false };
    case LOAD_MORE_LEAD_LISTS_REQUEST:
      return { ...state, leadListsLoading: true };
    case LOAD_MORE_LEAD_LISTS_SUCCESS:
      return { ...state, leadLists: unionBy([...state.leadLists, ...action.response.data], 'id'), leadListsMetaData: action.response.metadata, leadListsLoading: false };
    case LOAD_MORE_LEAD_LISTS_FAILURE:
      return { ...state, leadListsLoading: false };
    case GET_FILTERS_REQUEST:
      return { ...state, filtersLoading: true };
    case GET_FILTERS_SUCCESS:
      return { ...state, filters: unionBy([...state.filters, ...action.response.data], 'id'), filtersMetaData: action.response.metadata, filtersLoading: false };
    case GET_FILTERS_FAILURE:
      return { ...state, filtersLoading: false };
    case ADD_FILTER_REQUEST:
      return { ...state, addFilterLoading: true };
    case ADD_FILTER_SUCCESS:
      return { ...state, filters: [action.response, ...state.filters], addFilterLoading: false };
    case ADD_FILTER_FAILURE:
      return { ...state, addFilterLoading: false };
    case TOGGLE_FILTER_REQUEST:
      return { ...state, toggleFilterLoading: true };
    case TOGGLE_FILTER_SUCCESS:
      return { ...state, filters: state.filters.map(filter => filter.id === action.response.id ? { ...filter, is_active: action.response.is_active } : filter), toggleFilterLoading: false };
    case TOGGLE_FILTER_FAILURE:
      return { ...state, toggleFilterLoading: false };
    case DELETE_FILTER_REQUEST:
      return { ...state, deleteFilterLoading: true };
    case DELETE_FILTER_SUCCESS:
      return { ...state, filters: state.filters.filter(filter => filter.id !== action.id), deleteFilterLoading: false };
    case DELETE_FILTER_FAILURE:
      return { ...state, deleteFilterLoading: false };
    case GET_FILTERED_LEADS_REQUEST:
      return { ...state, filteredLeadsLoading: true };
    case GET_FILTERED_LEADS_SUCCESS:
      return { ...state, filteredLeads: action.response.results, filteredLeadsMetaData: {}, filteredLeadsLoading: false };
    case GET_FILTERED_LEADS_FAILURE:
      return { ...state, filteredLeadsLoading: false };
    case GET_STATS_REQUEST:
      return { ...state, statsLoading: true };
    case GET_STATS_SUCCESS:
      return { ...state, stats: action.response, statsLoading: false };
    case GET_STATS_FAILURE:
      return { ...state, statsLoading: false };
    case CLEAR_LEADS:
      return { ...state, leadLists: [] };
    case CLEAR_FILTERS:
      return { ...state, filters: [], filtersMetaData: {} };
    case GET_LEAD_INFO_REQUEST:
      return { ...state, leadInfoLoading: true };
    case GET_LEAD_INFO_SUCCESS:
      return { ...state, leadInfo: action.response, leadInfoLoading: false };
    case GET_LEAD_INFO_FAILURE:
      return { ...state, leadInfoLoading: false };
    case DELETE_LEAD_LIST_SUCCESS:
      return { ...state, leadLists: state.leadLists.filter(list => list.id !== action.id), leadListsMetaData: { ...state.leadListsMetaData, count: state.leadListsMetaData.count - 1 } };
    case GET_FILTERS_STATUS_SUCCESS:
      return { ...state, filters: state.filters.map(filter => {
        const _filter = action.response.data.find((status: any) => status.id === filter.id);
        if (_filter) {
          return ({ ...filter, status: _filter.status, filtered_count: _filter.filtered_count });
        }
        return filter;
      }) };
    case SYNC_LEAD_STATUS_SUCCESS:
      return { ...state, leadLists: state.leadLists.map(list => {
        const _list = action.response.data.find((status: any) => status.id === list.id);
        if (_list) {
          return ({ ...list, status: _list.status, row_count: _list.row_count, filtered_rows: _list.filtered_rows });
        }
        return list;
      }) };
    case SYNC_STATS_SUCCESS:
      return { ...state, stats: action.response };
    default:
      return state;
  }
}